import { FC, Fragment, type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimeDisplayFormat } from '@/entities/settings';
import { parseMarkedText, richTextToPlain } from '@/shared/lib';
import {
  sanitizeRichTextHtml,
  TEXT_DIFF_ADDED_CLASS,
  TEXT_DIFF_LEADING_CLASS,
  TEXT_DIFF_REMOVED_CLASS,
} from '@/shared/ui';
import { buildCommentDiffMarkup, sliceCommentDiff } from './commentDiff';
import { CommentTextLines } from './CommentTextLines';
import { type CommentMarker, EVALUATION_COMMENT_MARKERS } from './commentMarkers';
import { HtmlCommentContent } from './HtmlCommentContent';
import { KnowledgeNodeDetailDialog } from './KnowledgeNodeDetailDialog';
import { RuleDetailDialog } from './RuleDetailDialog';
import { TimeFragmentLink } from './TimeFragmentLink';

/** Комментарий с разметкой: приходит от модели и остаётся после правки в редакторе */
const HTML_TAG_PATTERN = /<[a-z][\s\S]*>/i;

type Props = {
  comment: string;
  expertReportId: number;
  blockId: number;
  /**
   * Версия комментария, составленная системой. Задана — комментарий показывается сравнением с ней:
   * убранное перечёркнуто, добавленное подсвечено.
   */
  previousComment?: string;
};

export const EvaluationCommentText: FC<Props> = ({ comment, expertReportId, blockId, previousComment }) => {
  const { t } = useTranslation('expertReport');
  const timeDisplayFormat = useTimeDisplayFormat('expertReport');
  const isHtml = useMemo(() => HTML_TAG_PATTERN.test(comment), [comment]);
  const [knowledgeNodeDialogId, setKnowledgeNodeDialogId] = useState<number | undefined>();
  const [ruleDialogId, setRuleDialogId] = useState<number | undefined>();
  const handleKnowledgeNodeClick = useCallback((id: number) => setKnowledgeNodeDialogId(id), []);
  const handleRuleClick = useCallback((id: number) => setRuleDialogId(id), []);

  const diffMarkup = useMemo(() => {
    if (previousComment === undefined) return null;
    return buildCommentDiffMarkup(
      richTextToPlain(sanitizeRichTextHtml(previousComment)),
      richTextToPlain(sanitizeRichTextHtml(comment)),
    );
  }, [previousComment, comment]);

  const renderMarker = useCallback(
    (marker: CommentMarker, index: number) => {
      if (marker.type === 'rule') {
        return (
          <span
            key={index}
            role="button"
            tabIndex={-1}
            className="text-primary underline cursor-pointer hover:text-primary/80"
            onClick={() => handleRuleClick(marker.id)}
          >
            {marker.text}
          </span>
        );
      }

      if (marker.type === 'knowledgeNode') {
        return (
          <span
            key={index}
            role="button"
            tabIndex={-1}
            className="text-xs text-primary underline cursor-pointer hover:text-primary/80"
            onClick={() => handleKnowledgeNodeClick(marker.id)}
          >
            {t('commentKnowledgeSource')}
          </span>
        );
      }

      return <TimeFragmentLink key={index} time={marker.time} format={timeDisplayFormat} />;
    },
    [handleRuleClick, handleKnowledgeNodeClick, t, timeDisplayFormat],
  );

  /** Текст с распознанными маркерами — им набирается каждый кусок сравнения */
  const renderMarkedText = useCallback(
    (text: string): ReactNode[] =>
      parseMarkedText(text, EVALUATION_COMMENT_MARKERS).map((part, index) =>
        part.kind === 'text' ? <CommentTextLines key={index} text={part.text} /> : renderMarker(part.marker, index),
      ),
    [renderMarker],
  );

  const renderDiffText = useCallback(
    (text: string, offset: number): ReactNode => {
      if (!diffMarkup) return null;
      return sliceCommentDiff(diffMarkup, text, offset).map((piece, index) => {
        if (piece.kind === 'added')
          return (
            <mark key={index} className={TEXT_DIFF_ADDED_CLASS}>
              {renderMarkedText(piece.text)}
            </mark>
          );
        if (piece.kind === 'removed')
          return (
            <s key={index} className={TEXT_DIFF_REMOVED_CLASS}>
              {renderMarkedText(piece.text)}
            </s>
          );
        return <Fragment key={index}>{renderMarkedText(piece.text)}</Fragment>;
      });
    },
    [diffMarkup, renderMarkedText],
  );

  const dialogs = (
    <>
      <KnowledgeNodeDetailDialog nodeId={knowledgeNodeDialogId} onClose={() => setKnowledgeNodeDialogId(undefined)} />
      <RuleDetailDialog
        ruleId={ruleDialogId}
        expertReportId={expertReportId}
        blockId={blockId}
        onClose={() => setRuleDialogId(undefined)}
      />
    </>
  );

  if (diffMarkup) {
    return (
      <>
        <div className={TEXT_DIFF_LEADING_CLASS}>
          <HtmlCommentContent
            html={comment}
            markers={EVALUATION_COMMENT_MARKERS}
            renderMarker={renderMarker}
            renderText={renderDiffText}
          />
          {/* Комментарий, стёртый правкой целиком, показывать не на чем: текста, к которому привязан убранный, не осталось */}
          {diffMarkup.length === 0 && (
            <s className="text-muted-foreground">
              {renderMarkedText(diffMarkup.removed.map((mark) => mark.text).join(''))}
            </s>
          )}
        </div>
        {dialogs}
      </>
    );
  }

  if (isHtml) {
    return (
      <>
        <HtmlCommentContent html={comment} markers={EVALUATION_COMMENT_MARKERS} renderMarker={renderMarker} />
        {dialogs}
      </>
    );
  }

  return (
    <>
      <span>{renderMarkedText(comment)}</span>
      {dialogs}
    </>
  );
};
